import type { Audience, TopicProfile } from '@/cms/topics';
import { themesForAudience } from '@/cms/topics';

/**
 * The journey's rows and their states, as pure functions.
 *
 * The server loader cannot do any of this: it has no idea who the learner is
 * (the profile lives in `localStorage`) or which modules they have finished.
 * So it ships the thinnest possible row per topic and the client filters,
 * orders and states them here.
 */

/** The two fields a row is filtered on, plus the sort key. */
export type JourneyRow = {
  uid: string;
  moduleKey: string;
  /** The topic's `theme` Select value — `Kids`, `Teen`, `Adult`, `Teen/Adult`. */
  theme: string;
  /** Lower-case language code, resolved from `language_ref` or the legacy Select. */
  language: string;
  order: number | null;
};

/**
 * One module as the journey renders it. Deliberately flat and serialisable:
 * the loader ships these instead of whole Prismic documents, which are two
 * orders of magnitude larger and mostly slices the journey never reads.
 */
export type JourneyModule = JourneyRow & {
  title: string;
  description: string | null;
  durationMinutes: number | null;
  iconUrl: string | null;
  iconAlt: string;
};

/** Where a module sits in the learner's journey. */
export type TopicState = 'done' | 'next' | 'todo';

/**
 * How far a part-finished module had played, as saved by `chat-progress`.
 *
 * Deliberately not a `TopicState`: "started" is orthogonal to where a module
 * sits in the journey. The first unfinished module is still the recommended
 * one whether or not it has been opened, and a started module further down the
 * trail is still `todo` — it just has something to show.
 */
export type StepProgress = { stepsDone: number; stepsTotal: number; fraction: number };

export type JourneyStep<T> = T & {
  state: TopicState;
  /** 1-based, for "Module 2 of 4". */
  position: number;
  /** Set only for a module that was started and not finished. */
  progress: StepProgress | null;
};

export type Journey<T> = {
  steps: Array<JourneyStep<T>>;
  /** How many modules have a score. */
  done: number;
  total: number;
  allDone: boolean;
  /** The recommended next module — the first without a score. */
  next: JourneyStep<T> | null;
};

/**
 * The rows this profile may see, ordered by `order` (unordered last).
 *
 * The client-side twin of `validTopicsFor`, which needs whole Prismic
 * documents. Both read the audience → `theme` map from `themesForAudience`, so
 * the rule itself is still defined once.
 */
export function journeyRowsFor<T extends JourneyRow>(profile: TopicProfile, rows: Array<T>): Array<T> {
  const themes = themesForAudience(profile.audience);
  const language = profile.language.toLowerCase();

  return rows
    .filter((row) => themes.includes(row.theme) && row.language === language)
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
}

/** A saved run reduced to what the journey draws, or null if there is nothing to draw. */
function progressFor(saved: { stepsDone: number; stepsTotal: number } | undefined): StepProgress | null {
  if (!saved || saved.stepsTotal <= 0 || saved.stepsDone <= 0) {
    return null;
  }

  const stepsDone = Math.min(saved.stepsDone, saved.stepsTotal);

  return { stepsDone, stepsTotal: saved.stepsTotal, fraction: stepsDone / saved.stepsTotal };
}

/**
 * Joins ordered rows with the set of finished `module_key`s: everything with a
 * score is `done`, the first without one is `next`, the rest are `todo`.
 *
 * A `todo` module is still tappable — this only drives styling and which card
 * carries the primary action.
 *
 * `startedKeys` carries the part-finished runs. A finished module never shows
 * progress: its score is the better statement, and a stale run would otherwise
 * outlive the module it belongs to.
 */
export function deriveJourney<T extends JourneyRow>(
  rows: Array<T>,
  doneKeys: ReadonlySet<string>,
  startedKeys: ReadonlyMap<string, { stepsDone: number; stepsTotal: number }> = new Map(),
): Journey<T> {
  let nextTaken = false;

  const steps = rows.map((row, index): JourneyStep<T> => {
    if (doneKeys.has(row.moduleKey)) {
      return { ...row, state: 'done', position: index + 1, progress: null };
    }

    const progress = progressFor(startedKeys.get(row.moduleKey));

    if (nextTaken) {
      return { ...row, state: 'todo', position: index + 1, progress };
    }

    nextTaken = true;

    return { ...row, state: 'next', position: index + 1, progress };
  });

  const done = steps.filter((step) => step.state === 'done').length;

  return {
    steps,
    done,
    total: steps.length,
    // An empty journey is never complete: it means the topics have not loaded.
    allDone: steps.length > 0 && done === steps.length,
    next: steps.find((step) => step.state === 'next') ?? null,
  };
}

/** Adults fill in the Stack9 form; under-18s never share personal details. */
export function needsPassportForm(audience: Audience): boolean {
  return audience === 'adult';
}
